import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Copy } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

// Studio-wide Notion connection (public OAuth integration, read-only)
export function NotionCard() {
    const [st, setSt] = useState(null);
    const [cid, setCid] = useState("");
    const [sec, setSec] = useState("");
    const [editApp, setEditApp] = useState(false);
    const [params, setParams] = useSearchParams();
    const redirect = `${window.location.origin}/api/integrations/notion/callback`;
    const load = async () => { try { setSt((await api.get("/integrations/notion")).data); } catch (e) { toast.error(extractError(e)); } };
    useEffect(() => {
        load();
        const n = params.get("notion");
        if (n === "ok") toast.success("Notion collegato");
        if (n === "error") toast.error("Collegamento Notion non riuscito o annullato");
        if (n) { params.delete("notion"); setParams(params, { replace: true }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!st) return null;
    const act = async (fn, ok) => { try { setSt((await fn()).data); if (ok) toast.success(ok); } catch (e) { toast.error(extractError(e)); } };
    const saveApp = () => act(() => api.put("/integrations/notion/app", { client_id: cid, client_secret: sec }), "Credenziali Notion salvate").then(() => { setEditApp(false); setSec(""); });
    const connect = async () => {
        try { window.location.href = (await api.post("/integrations/notion/start")).data.authorization_url; } catch (e) { toast.error(extractError(e)); }
    };
    const showApp = !st.app_configured || editApp;
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-5 flex items-start gap-4" data-testid="integration-notion">
            <BookOpen size={20} className="text-neutral-500 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="font-semibold text-sm">Notion</div>
                        <div className="text-xs text-neutral-500" data-testid="integration-notion-status">
                            {st.connected ? `Collegato allo spazio "${st.workspace_name || "Notion"}" · sola lettura` : st.app_configured ? "Credenziali inserite: ora collega lo spazio di lavoro." : "Non collegato. La Time Machine funziona comunque, senza le pagine Notion."}
                        </div>
                        {st.last_error && <div className="text-xs text-red-600 mt-1" data-testid="integration-notion-error">Ultimo errore: {st.last_error}</div>}
                    </div>
                    {st.connected ? (
                        <div className="flex items-center gap-4 shrink-0">
                            <label className="flex items-center gap-2 text-xs text-neutral-600">
                                <Switch checked={st.enabled} onCheckedChange={(v) => act(() => api.patch("/integrations/notion/toggle", { enabled: v }))} data-testid="integration-notion-toggle" /> {st.enabled ? "Attivo" : "Disattivato"}
                            </label>
                            <button onClick={() => window.confirm("Scollegare Notion?") && act(() => api.delete("/integrations/notion/connection"))} data-testid="integration-notion-disconnect" className="text-xs text-neutral-500 hover:text-red-600">Scollega</button>
                        </div>
                    ) : st.app_configured && !editApp && (
                        <button onClick={connect} data-testid="integration-notion-connect" className="text-xs px-3 py-1.5 rounded-md bg-neutral-900 text-white shrink-0">Collega Notion</button>
                    )}
                </div>
                {showApp && (
                    <div className="space-y-2 pt-1">
                        <div className="text-xs text-neutral-600">URI di reindirizzamento da inserire nell'integrazione Notion:</div>
                        <div className="flex items-center gap-2">
                            <code className="text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1 flex-1 truncate" data-testid="notion-redirect-uri">{redirect}</code>
                            <button onClick={() => { navigator.clipboard.writeText(redirect); toast.success("Copiato"); }} data-testid="notion-redirect-copy" className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"><Copy size={12} /> Copia</button>
                        </div>
                        <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="OAuth client ID" data-testid="notion-client-id" className="w-full text-sm border border-neutral-200 rounded-md px-3 py-1.5" />
                        <input type="password" value={sec} onChange={(e) => setSec(e.target.value)} placeholder="OAuth client secret" data-testid="notion-client-secret" className="w-full text-sm border border-neutral-200 rounded-md px-3 py-1.5" />
                        <div className="flex gap-2 justify-end">
                            {editApp && <button onClick={() => setEditApp(false)} className="text-xs px-3 py-1.5 rounded-md border border-neutral-200">Annulla</button>}
                            <button onClick={saveApp} disabled={!cid.trim() || !sec.trim()} data-testid="notion-app-save" className="text-xs px-3 py-1.5 rounded-md bg-neutral-900 text-white disabled:opacity-40">Salva credenziali</button>
                        </div>
                    </div>
                )}
                {st.app_configured && !showApp && !st.connected && (
                    <button onClick={() => { setCid(st.client_id || ""); setEditApp(true); }} data-testid="notion-app-edit" className="text-xs text-neutral-500 underline">Modifica credenziali</button>
                )}
            </div>
        </div>
    );
}
